import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { API_BASE_URL } from '../config/api';
import { Store, Lock, Package, Briefcase, MapPin, Link2, CheckCircle } from 'lucide-react';

export default function SellerStorefront() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    axios.get(`${API_BASE_URL}/api/market/sellers/${id}/storefront`,
      token ? { headers: { Authorization: `Bearer ${token}` } } : {})
      .then(({ data }) => setData(data))
      .catch(() => setNotFound(true));
  }, [id]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      window.prompt('Copy this link:', window.location.href);
    });
  };

  if (notFound) {
    return (
      <div className="min-h-screen"><Header />
        <div className="pt-40 pb-20 text-center text-gray-500">This storefront does not exist.</div>
        <Footer /></div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen"><Header />
        <div className="pt-40 pb-20 text-center text-gray-500">Loading storefront...</div>
        <Footer /></div>
    );
  }

  const s = data.seller;
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="bg-gradient-to-br from-[#1E3A5F] to-[#1E3A5F]/90 text-white py-16 pt-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-[#E87722] text-sm font-semibold mb-2">
            <Store className="h-4 w-4" />CSED MARKET PLACE SELLER
          </div>
          <h1 className="text-3xl md:text-5xl font-bold" data-testid="storefront-title">{s.org || s.name}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-3 text-sm">
            {s.rating && (
              <span className="text-amber-400 font-medium" data-testid="storefront-rating">
                {'★'.repeat(Math.round(s.rating.avg))}{'☆'.repeat(5 - Math.round(s.rating.avg))} {s.rating.avg} ({s.rating.count} rating{s.rating.count === 1 ? '' : 's'})
              </span>
            )}
            <span className="text-gray-300">{data.listing_count} listing{data.listing_count === 1 ? '' : 's'}</span>
            {s.member_since && <span className="text-gray-300">Seller since {s.member_since}</span>}
            {!s.active && <Badge className="bg-red-100 text-red-700 text-[10px]">Currently inactive</Badge>}
          </div>
          <Button size="sm" variant="outline" onClick={copyLink}
            className="mt-4 border-white/40 text-white hover:bg-white/10 bg-transparent" data-testid="copy-storefront-link">
            {copied ? <><CheckCircle className="h-3.5 w-3.5 mr-1.5" />Link copied</> : <><Link2 className="h-3.5 w-3.5 mr-1.5" />Copy shareable link</>}
          </Button>
        </div>
      </section>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {data.is_member && s.active ? (
          data.listings.length ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {data.listings.map(l => (
                <Card key={l.id} className="overflow-hidden hover:shadow-xl transition-shadow cursor-pointer flex flex-col"
                  onClick={() => navigate('/marketplace')} data-testid={`storefront-listing-${l.id}`}>
                  {l.image_url ? (
                    <img src={l.image_url} alt={l.title} className="h-40 w-full object-cover" />
                  ) : (
                    <div className="h-40 w-full bg-[#1E3A5F]/5 flex items-center justify-center">
                      {l.type === 'product' ? <Package className="h-10 w-10 text-[#1E3A5F]/30" /> : <Briefcase className="h-10 w-10 text-[#1E3A5F]/30" />}
                    </div>
                  )}
                  <div className="p-4 flex-1">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Badge className={l.type === 'product' ? 'bg-[#E87722]/10 text-[#E87722] text-[10px]' : 'bg-[#1E3A5F]/10 text-[#1E3A5F] text-[10px]'}>
                        {l.type === 'product' ? 'Product' : 'Service'}
                      </Badge>
                      <span className="text-xs text-gray-500">{l.category}</span>
                    </div>
                    <h3 className="font-bold text-[#1E3A5F] leading-snug">{l.title}</h3>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-3">{l.description}</p>
                    {l.price_display && <p className="text-sm font-semibold text-[#E87722] mt-2">{l.price_display}</p>}
                    {l.location && <p className="text-xs text-gray-500 flex items-center gap-1 mt-1"><MapPin className="h-3 w-3" />{l.location}</p>}
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-10 text-center text-gray-500">This seller has no live listings yet.</Card>
          )
        ) : (
          <Card className="max-w-lg mx-auto p-8 text-center" data-testid="storefront-locked-card">
            <Lock className="h-10 w-10 mx-auto text-[#E87722] mb-4" />
            <h2 className="text-lg font-bold text-[#1E3A5F] mb-2">
              {s.active ? 'Full listings are exclusive to CSED Members' : 'This storefront is temporarily inactive'}
            </h2>
            <p className="text-gray-600 text-sm mb-6">
              {s.active
                ? `${s.org || s.name} has ${data.listing_count} listing(s) on the CSED Market Place. Log in with your member account or become a member to view details and enquire.`
                : 'The seller\'s account is awaiting renewal. Please check back soon.'}
            </p>
            {s.active && (
              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={() => navigate('/login')} data-testid="storefront-login-btn">Login</Button>
                <Button className="bg-[#E87722] hover:bg-[#d66a1a]" onClick={() => navigate('/membership')} data-testid="storefront-member-btn">
                  Become a Member
                </Button>
              </div>
            )}
          </Card>
        )}
        <div className="text-center mt-10">
          <Button variant="outline" onClick={() => navigate('/marketplace')} data-testid="storefront-open-market">
            <Store className="h-4 w-4 mr-2" />Open the Market Place
          </Button>
        </div>
      </main>
      <Footer />
    </div>
  );
}
